import { useMemo, useState } from 'react';

/**
 * Build a forest from the flat department list.
 *
 * A department whose parent is absent from the list (not synced, or filtered
 * out by the company scope) is promoted to the top rather than dropped: a
 * missing parent must never hide a department that has people in it.
 *
 * Cycle safe. parent_id is written only by the Odoo sync, which refuses
 * self-references, but this runs in the render path and cannot lean on an
 * invariant owned by another module — a cycle here would hang the page.
 */
export function buildDeptTree(departments) {
  const byId = new Map(departments.map((d) => [d.id, { ...d, children: [] }]));
  const roots = [];
  for (const d of byId.values()) {
    const parent = d.parent_id != null ? byId.get(d.parent_id) : null;
    if (parent && parent.id !== d.id) parent.children.push(d);
    else roots.push(d);
  }

  const reachable = new Set();
  const stack = [...roots];
  while (stack.length > 0) {
    const cur = stack.pop();
    if (reachable.has(cur.id)) continue;
    reachable.add(cur.id);
    for (const c of cur.children) stack.push(c);
  }
  // Anything unreached is in a cycle: every member is some other member's
  // child, so the group has no root. Promote one and cut the link that made it
  // a child, or it would render twice.
  for (const d of byId.values()) {
    if (reachable.has(d.id)) continue;
    const parent = d.parent_id != null ? byId.get(d.parent_id) : null;
    if (parent) {
      const i = parent.children.indexOf(d);
      if (i !== -1) parent.children.splice(i, 1);
    }
    roots.push(d);
    const inner = [d];
    while (inner.length > 0) {
      const cur = inner.pop();
      if (reachable.has(cur.id)) continue;
      reachable.add(cur.id);
      for (const c of cur.children) inner.push(c);
    }
  }

  const sortRec = (list) => {
    list.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of list) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Every id in a subtree, the node itself included. Cycle safe. */
function subtreeIds(node, out = new Set()) {
  if (out.has(node.id)) return out;
  out.add(node.id);
  for (const c of node.children) subtreeIds(c, out);
  return out;
}

/**
 * Department filter rail for the employee directory.
 *
 * Nested by parent department, because a flat alphabetical list of 67 rows
 * could not show that "Inbound - Client Growth" sits under "Client Growth -
 * Growth" under "Growth", and gave no way to ask for a whole branch. Selecting
 * a parent selects its SUBTREE — the server expands the ids — so the count on a
 * parent row is the rolled-up total and always matches what clicking returns.
 *
 * Multi-select checkboxes rather than a single-choice dropdown: people look for
 * "anyone in Data or Data Architecture", which one dropdown value cannot express.
 * Selecting nothing means no filter, so the default state needs no "All" option
 * to be checked — a "Clear" button resets it instead.
 *
 * On narrow screens (≤900px) the rail stacks above the results, where a fully
 * expanded tree filled the whole first screen and pushed the people search and
 * every card below the fold. There it starts as a closed disclosure; on desktop
 * the toggle is hidden and the body is always shown.
 */
export default function DepartmentFilterSidebar({
  departments,
  selected,
  onToggle,
  onClear,
  loading = false,
  error = false,
  onRetry,
}) {
  const [filter, setFilter] = useState('');
  // Narrow screens only (the toggle is display:none on desktop).
  const [open, setOpen] = useState(false);
  // Branches the reader has collapsed. Empty by default: the rail opens fully
  // expanded so nothing is hidden behind a disclosure on first paint.
  const [collapsed, setCollapsed] = useState(() => new Set());

  const { roots, noSearchMatch } = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matches = needle
      ? departments.filter((d) => d.name.toLowerCase().includes(needle))
      : departments;

    // Every department is shown, empty ones included. They used to sit behind a
    // "Show N empty departments" toggle, which suited a flat list but not a
    // tree: an empty department is usually a STRUCTURAL node (Value, SMO,
    // Segments - Value) whose purpose is to hold the staffed teams beneath it,
    // so hiding it strands its children or hides the branch being looked for.
    return {
      roots: buildDeptTree(matches),
      noSearchMatch: needle !== '' && matches.length === 0,
    };
  }, [departments, filter]);

  // Ids covered by a checked ANCESTOR. Selecting a parent selects its subtree
  // server side, so a descendant of a selection is already included and is shown
  // as such rather than looking unselected. Computed once per render over the
  // whole forest — doing it per row rebuilt the tree for every row.
  const coveredIds = useMemo(() => {
    if (selected.length === 0) return new Set();
    const all = buildDeptTree(departments);
    const index = new Map();
    const walk = (n) => { index.set(n.id, n); n.children.forEach(walk); };
    all.forEach(walk);
    const out = new Set();
    for (const id of selected) {
      const node = index.get(id);
      if (!node) continue;
      for (const descendant of subtreeIds(node)) {
        if (descendant !== id) out.add(descendant);
      }
    }
    return out;
  }, [departments, selected]);

  const hasSelection = selected.length > 0;

  const toggleCollapsed = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (node, depth) => {
    const checked = selected.includes(node.id);
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(node.id);
    // A parent selects its whole branch, so its row shows the rolled-up total.
    // A leaf's own count and its total are the same number.
    const count = hasChildren ? (node.total_count ?? node.member_count) : node.member_count;
    // Only meaningful on a parent that also holds people directly.
    const showsOwn = hasChildren && node.member_count > 0;
    // A checked ancestor already includes this row, so a descendant of a
    // selection reads as included without being separately checked.
    const covered = !checked && coveredIds.has(node.id);

    return (
      <li key={node.id}>
        <div
          className={`dir-side__row ${checked ? 'is-checked' : ''} ${covered ? 'is-covered' : ''}`}
          style={{ '--dir-depth': depth }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="dir-side__twisty"
              aria-expanded={!isCollapsed}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${node.name}`}
              onClick={() => toggleCollapsed(node.id)}
            >
              <svg className="dir-side__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
          ) : (
            <span className="dir-side__twisty dir-side__twisty--leaf" aria-hidden="true" />
          )}
          <label className="dir-side__item">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(node.id)}
            />
            <span className="dir-side__name" title={node.name}>
              {node.name}
              {covered && <span className="sr-only"> (included by a selected parent)</span>}
            </span>
            <span
              className="dir-side__count"
              title={showsOwn
                ? `${count} people in this branch, ${node.member_count} directly in ${node.name}`
                : `${count} ${count === 1 ? 'person' : 'people'}`}
            >
              <span aria-hidden={showsOwn ? 'true' : undefined}>{count}</span>
              {showsOwn && <span className="dir-side__own" aria-hidden="true"> ({node.member_count})</span>}
              {/* The "(n)" suffix is shorthand; this is what it means. */}
              {showsOwn && (
                <span className="sr-only">
                  {`${count} people in this branch, ${node.member_count} directly in ${node.name}`}
                </span>
              )}
            </span>
          </label>
        </div>
        {hasChildren && !isCollapsed && (
          <ul className="dir-side__list dir-side__list--nested">
            {node.children.map((c) => renderNode(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  const bodyId = 'dir-side-body';
  return (
    <aside className="dir-side" aria-label="Filter by department">
      <button
        type="button"
        className="dir-side__toggle"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          Filter by department
          {hasSelection && <span className="dir-side__toggle-count"> · {selected.length} selected</span>}
          {/* On a phone the rail starts closed, so a failed read must show here
              or it reads as a normal, working filter. */}
          {!loading && error && <span className="dir-side__toggle-error"> · couldn&apos;t load</span>}
        </span>
        <svg className="dir-side__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="m9 6 6 6-6 6" />
        </svg>
      </button>

      <div id={bodyId} className={`dir-side__body${open ? ' is-open' : ''}`}>
        <div className="dir-side__head">
          <h2 className="dir-side__title">Departments</h2>
          {hasSelection && (
            <button type="button" className="dir-side__clear" onClick={onClear}>
              Clear{selected.length > 1 ? ` (${selected.length})` : ''}
            </button>
          )}
        </div>

        {departments.length > 12 && (
          <input
            type="search"
            className="search-input dir-side__search"
            placeholder="Find a department…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Find a department"
          />
        )}

        {loading && <p className="dir-side__note" role="status">Loading departments…</p>}

        {!loading && error && (
          <div className="dir-side__note dir-side__note--error" role="alert">
            <p>Couldn&apos;t load departments.</p>
            {onRetry && (
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={onRetry}
                aria-label="Retry loading departments"
              >
                Try again
              </button>
            )}
          </div>
        )}

        {!loading && !error && departments.length === 0 && (
          <p className="dir-side__note">No departments to filter by.</p>
        )}

        {!loading && departments.length > 0 && (
          <>
            <ul className="dir-side__list">
              {roots.map((r) => renderNode(r, 0))}
            </ul>

            {noSearchMatch && (
              <p className="dir-side__note">No department matches that.</p>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
